import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import { TRANSPORT_REQUEST_STATUS_TR, trStatusVariant } from "@/lib/labels";

const route = (r) => `${r.pickup_province}/${r.pickup_district} → ${r.delivery_province}/${r.delivery_district}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");

const LinkRequestCard = ({ r, onDecide, busy }) => (
  <div className="rounded-2xl border-2 border-primary/60 bg-primary/5 p-3 space-y-2" data-testid={`yh-link-req-${r.id}`}>
    <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="w-4 h-4" />Yol Hattı sizin adınıza bir taşıma talebi oluşturdu</div>
    <div className="text-sm"><b>{route(r)}</b> · {r.transport_category} · {r.quantity} adet</div>
    <div className="text-xs text-muted-foreground">Son alım: {fmt(r.deadline_at)} · Onay için son: {fmt(r.link_expires_at)}</div>
    <div className="text-xs">Onaylarsanız bu talep hesabınıza bağlanır ve durumunu buradan takip edersiniz. Teklifler ve iletişim Yol Hattı üzerinden yürür.</div>
    <div className="flex gap-2">
      <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, "approve")} data-testid={`yh-link-approve-${r.id}`} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold"><Check className="w-4 h-4 mr-1" />Hesabıma bağla</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r.id, "reject")} data-testid={`yh-link-reject-${r.id}`}><X className="w-4 h-4 mr-1" />Benim değil</Button>
    </div>
  </div>
);

const LinkedCard = ({ r }) => (
  <div className="rounded-2xl border p-3" data-testid={`yh-linked-${r.id}`}>
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="text-sm flex items-center gap-1.5"><Badge className="bg-[hsl(var(--brand-black))] text-primary hover:bg-[hsl(var(--brand-black))] text-[10px]">Yol Hattı</Badge><b>{route(r)}</b></div>
      <Badge variant={trStatusVariant(r.status)} data-testid={`yh-linked-status-${r.id}`}>{TRANSPORT_REQUEST_STATUS_TR[r.status] || r.status}</Badge>
    </div>
    <div className="text-xs text-muted-foreground">{r.transport_category} · {r.quantity} adet · Son alım: {fmt(r.deadline_at)}</div>
    {(r.assigned_vehicle || r.assigned_driver) && <div className="text-xs mt-1">Araç: {r.assigned_vehicle?.name || "—"} · Şoför: {r.assigned_driver?.name || "—"}</div>}
    <div className="text-xs text-muted-foreground mt-1">Sorularınız için Yol Hattı ile iletişime geçin.</div>
  </div>
);

export const YhCustomerLinked = ({ onHasItems }) => {
  const [reqs, setReqs] = useState([]);
  const [linked, setLinked] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/customer/transport/yh-link-requests").then(({ data }) => setReqs(data)).catch(() => {});
    api.get("/customer/transport/yh-linked").then(({ data }) => setLinked(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  useEffect(() => { if (onHasItems) onHasItems(reqs.length + linked.length > 0); }, [reqs, linked, onHasItems]);

  const decide = async (rid, decision) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/customer/transport/yh-link-requests/${rid}/${decision}`);
      toast.success(decision === "approve" ? "Talep hesabınıza bağlandı" : "İstek reddedildi");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!reqs.length && !linked.length) return null;
  return (
    <div className="grid gap-2 mb-2" data-testid="yh-customer-linked">
      {reqs.map((r) => <LinkRequestCard key={r.id} r={r} onDecide={decide} busy={busy} />)}
      {linked.map((r) => <LinkedCard key={r.id} r={r} />)}
    </div>
  );
};
